import { useEffect, useState, memo, useCallback } from "react";
import {
    ReactFlow,
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { LuShip, LuPackage, LuLink2, LuTrash2, LuX, LuTruck, LuCalendar, LuAnchor, LuMapPin, LuCalculator, LuArrowRightLeft, LuChevronLeft, LuChevronRight, LuCheck } from "react-icons/lu";
import { fetchCargoDetails, fetchVesselDetails, sendCalculatorData } from "./action";
import { getObject, toastError, toastSuccess } from "../../../../utils";
import { updateUser } from "../../Dashboard/actions";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { isUser30DaysOld } from "..";

const CargoNode = ({ data, id }) => {
    const handleDelete = (e) => {
        e.stopPropagation();
        data.onDelete(id);
    };

    return (
        <div className="bg-gradient-to-br from-rose-50 to-white rounded-lg shadow-md p-2.5 border-2 border-rose-400 min-w-[160px] hover:shadow-lg transition-all relative group">
            <Handle type="source" position={Position.Right} className="w-2.5 h-2.5 bg-rose-500" />
            <Handle type="target" position={Position.Left} className="w-2.5 h-2.5 bg-rose-500" />

            <button
                onClick={handleDelete}
                className="absolute -top-2 -right-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
                title="Delete node"
            >
                <LuX size={12} />
            </button>

            <div className="flex items-center gap-1.5 mb-1.5">
                <div className="p-1 bg-rose-100 rounded">
                    <LuPackage className="text-rose-600" size={14} />
                </div>
                <span className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider">Cargo</span>
            </div>
            <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1" title={data?.name}>
                    {data?.name}
                </h4>
            </div>
            <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs">
                    <LuTruck className="text-rose-500 flex-shrink-0" size={14} />
                    <span className="text-gray-600 truncate" title={data.freightMode}>
                        {data.freightMode}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                    <LuPackage className="text-rose-500 flex-shrink-0" size={14} />
                    <span className="text-gray-600 truncate" title={data.qtyMode}>
                        {data.qtyMode}
                    </span>
                </div>
            </div>
        </div>
    );
};

const VesselNode = ({ data, id }) => {
    const handleDelete = (e) => {
        e.stopPropagation();
        data.onDelete(id);
    };

    const vesselObj = data?.vessel && JSON.parse(data?.vessel);

    return (
        <div className="bg-gradient-to-br from-sky-50 to-white rounded-lg shadow-md p-2.5 border-2 border-sky-400 min-w-[160px] hover:shadow-lg transition-all relative group">
            <Handle type="source" position={Position.Right} className="w-2.5 h-2.5 bg-sky-500" />
            <Handle type="target" position={Position.Left} className="w-2.5 h-2.5 bg-sky-500" />

            <button
                onClick={handleDelete}
                className="absolute -top-2 -right-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
                title="Delete node"
            >
                <LuX size={12} />
            </button>

            <div className="flex items-center gap-1.5 mb-1.5">
                <div className="p-1 bg-sky-100 rounded">
                    <LuShip className="text-sky-600" size={14} />
                </div>
                <span className="text-[10px] font-semibold text-sky-600 uppercase tracking-wider">Vessel</span>
            </div>
            <div className="pl-2">
                <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1" title={vesselObj?.vesselName}>
                        {vesselObj?.vesselName}
                    </h4>
                </div>
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                        <LuAnchor className="text-sky-500 flex-shrink-0" size={14} />
                        <span className="text-gray-600 truncate" title={vesselObj?.vesselType}>
                            {vesselObj?.vesselType}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                        <LuMapPin className="text-sky-500 flex-shrink-0" size={14} />
                        <span className="text-gray-600 truncate" title={data?.vesselOpeningPort}>
                            {data?.vesselOpeningPort}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ConnectionNode = ({ data, id }) => {
    const handleDelete = (e) => {
        e.stopPropagation();
        data.onDelete(id);
    };

    const response = data.apiResponse?.data?.[data.vesselId];
    const vesselObj = data?.vessel && JSON.parse(data?.vessel.vessel);

    return (
        <div className="relative group rounded-xl shadow-md border border-emerald-200 bg-gradient-to-br from-white to-emerald-50 p-3 w-fit min-w-[350px] transition-all hover:shadow-xl">
            <Handle type="source" position={Position.Right} className="w-2.5 h-2.5 bg-emerald-500" />
            <Handle type="target" position={Position.Left} className="w-2.5 h-2.5 bg-emerald-500" />

            <button
                onClick={handleDelete}
                className="absolute -top-2 -right-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
                title="Delete connection"
            >
                <LuX size={12} />
            </button>

            {data.loading && (
                <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
                    <span className="ml-2 text-emerald-600 text-sm font-medium">
                        Calculating...
                    </span>
                </div>
            )}

            {data.error && (
                <div className="text-center text-rose-600 text-xs font-medium py-2">
                    {data.error}
                </div>
            )}

            {!data.loading && !data.error && (
                <div className="min-w-[440px]">
                    {response && (
                        <div className="p-2.5 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-lg border border-emerald-300 text-[11px] space-y-1.5 shadow-inner">
                            <div className="flex items-center gap-1.5 mb-1">
                                <LuCalculator className="text-emerald-600" size={12} />
                                <span className="font-semibold text-emerald-700">
                                    Calculation Summary
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-base">
                                <div>Frt Cost:</div>
                                <div className="text-right font-medium text-emerald-800">
                                    {response?.frtCost?.toFixed(2) || "0.00"}
                                </div>

                                <div>Profit PMT:</div>
                                <div className="text-right">{response?.profitPmt?.toFixed(2) || "0.00"}</div>

                                <div>Profit Net:</div>
                                <div className="text-right">{response?.profitNet?.toFixed(2) || "0.00"}</div>

                                <div>Net Frt Revenue:</div>
                                <div className="text-right">{response?.netFrtRevenue?.toFixed(2) || "0.00"}</div>

                                <div>Hire Exp:</div>
                                <div className="text-right">{response?.hireExpense?.toFixed(2) || "0.00"}</div>

                                <div>Fuel Exp:</div>
                                <div className="text-right">{response?.fuelExpense?.toFixed(2) || "0.00"}</div>

                                <div>Others:</div>
                                <div className="text-right">{response?.otherExpense?.toFixed(2) || "0.00"}</div>

                                <div>Main Fuel Qty:</div>
                                <div className="text-right">{response?.mainFuelQuantity?.toFixed(2) || "0.00"}</div>

                                <div>ECA Qty:</div>
                                <div className="text-right">{response?.ecaQuantity?.toFixed(2) || "0.00"}</div>

                                <div>MGO Qty:</div>
                                <div className="text-right">{response?.mgoQuantity?.toFixed(2) || "0.00"}</div>

                                <div>Voy Duration:</div>
                                <div className="text-right">{response?.voyageDuration?.toFixed(2) || "0.00"}</div>

                                <div>Port Stay:</div>
                                <div className="text-right">{response?.portStayTotal?.toFixed(2) || "0.00"}</div>

                                <div>Stowage:</div>
                                <div className="text-right">{response?.stowage?.toFixed(2) || "0.00"}</div>

                                <div>Fuel Settle Profit:</div>
                                <div className="text-right text-emerald-700 font-semibold">
                                    {response?.fuelSettlementProfit?.toFixed(2) || "0.00"}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-between items-center gap-2 mt-3">
                        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 w-full">
                            <div className="flex items-center gap-1 mb-1">
                                <LuPackage className="text-slate-600" size={12} />
                                <span className="text-[10px] font-semibold text-slate-600">
                                    CARGO
                                </span>
                            </div>

                            <div className="font-semibold text-gray-800 text-sm mb-2 truncate">
                                {data?.cargo?.name}
                            </div>

                            <div className="space-y-1.5 text-xs text-gray-600">
                                <div className="flex items-center gap-2">
                                    <LuTruck className="text-slate-500" size={13} />
                                    <span>{data?.cargo?.freightMode}</span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <LuPackage className="text-slate-500" size={13} />
                                    <span>{data?.cargo?.qtyMode}</span>
                                </div>

                                <div className="flex items-center gap-2 pt-1 border-t border-gray-100 text-gray-500">
                                    <LuCalendar size={13} />
                                    <span>{moment(data?.cargo?.createdAt).format("DD-MM-YYYY")}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center -mt-2">
                            <div className="text-emerald-400 text-sm"><LuArrowRightLeft /></div>
                        </div>

                        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 w-full">
                            <div className="flex items-center gap-1 mb-1">
                                <LuShip className="text-slate-600" size={12} />
                                <span className="text-[10px] font-semibold text-slate-600">
                                    VESSEL
                                </span>
                            </div>

                            <div className="font-semibold text-gray-800 text-sm mb-2 truncate">
                                {vesselObj?.vesselName}
                            </div>

                            <div className="space-y-1.5 text-xs text-gray-600">
                                <div className="flex items-center gap-2">
                                    <LuAnchor className="text-slate-500" size={13} />
                                    <span>{vesselObj?.vesselType}</span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <LuMapPin className="text-slate-500" size={13} />
                                    <span>{data?.vessel?.vesselOpeningPort}</span>
                                </div>

                                <div className="flex items-center gap-2 pt-1 border-t border-gray-100 text-gray-500">
                                    <LuCalendar size={13} />
                                    <span>{moment(data?.vessel?.createdDate).format("DD-MM-YYYY")}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2 pt-1.5 border-t border-emerald-200 text-[10px] text-gray-500 text-center">
                        {new Date(data.connectedAt).toLocaleTimeString()}
                    </div>
                </div>
            )}
        </div>
    );
};

const nodeTypes = {
    cargo: CargoNode,
    vessel: VesselNode,
    connection: ConnectionNode,
};

function MatrixCurve({
    setLoader,
    loader,
}) {
    const [cargoVesselList, setCargoVesselList] = useState({
        cargoList: [],
        vesselList: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const user = getObject("auth");
    const [selectedCargo, setSelectedCargo] = useState([]);
    const [selectedVessels, setSelectedVessels] = useState([]);
    // Phones: both w-56 panels cannot fit beside the diagram, so start closed and keep at most one open.
    const isPhone = () => window.matchMedia("(max-width: 767px)").matches;
    const [leftSidebarOpen, setLeftSidebarOpen] = useState(() => !isPhone());
    const [rightSidebarOpen, setRightSidebarOpen] = useState(() => !isPhone());
    const toggleLeftSidebar = (open) => {
        setLeftSidebarOpen(open);
        if (open && isPhone()) setRightSidebarOpen(false);
    };
    const toggleRightSidebar = (open) => {
        setRightSidebarOpen(open);
        if (open && isPhone()) setLeftSidebarOpen(false);
    };

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [connections, setConnections] = useState([]);
    const [filter, setFilter] = useState({
        perPage: 50,
        offset: 1,
    });
    const [reason, setReason] = useState("");

    const handleInitialFetch = async () => {
        try {
            setLoader(true);
            const cargoRes = await fetchCargoDetails(filter);
            const vesselRes = await fetchVesselDetails(filter);
            setCargoVesselList({
                cargoList: cargoRes?.data?.list.filter((item) => item?.isShowInCalculator) || [],
                vesselList: vesselRes?.data?.list || [],
            });
            setLoader(false);
        } catch (error) {
            toastError("Error fetching details");
            setLoader(false);
            console.error(error);
        }
    };

    useEffect(() => {
        handleInitialFetch();
    }, []);

    // Initialize with 5 items selected by default
    useEffect(() => {
        if (cargoVesselList.cargoList.length > 0 && selectedCargo.length === 0) {
            const defaultCargo = cargoVesselList.cargoList.slice(0, 5).map(c => c._id);
            setSelectedCargo(defaultCargo);
        }
        if (cargoVesselList.vesselList.length > 0 && selectedVessels.length === 0) {
            const defaultVessels = cargoVesselList.vesselList.slice(0, 5).map(v => v._id);
            setSelectedVessels(defaultVessels);
        }
    }, [cargoVesselList]);

    useEffect(() => {
        const cargoNodes = cargoVesselList.cargoList
            .filter(cargo => selectedCargo.includes(cargo._id))
            .map((cargo, index) => ({
                id: `cargo-${cargo._id}`,
                type: 'cargo',
                position: { x: 100, y: index * 200 + 50 },
                data: {
                    ...cargo,
                    onDelete: deleteNode
                },
            }));

        const vesselNodes = cargoVesselList.vesselList
            .filter(vessel => selectedVessels.includes(vessel._id))
            .map((vessel, index) => ({
                id: `vessel-${vessel._id}`,
                type: 'vessel',
                position: { x: 800, y: index * 200 + 50 },
                data: {
                    ...vessel,
                    onDelete: deleteNode
                },
            }));

        const connectionNodes = nodes.filter(n => {
            if (n.type !== 'connection') return false;

            const cargoNodeExists = cargoNodes.some(c => c.id === `cargo-${n.data.cargo._id}`);
            const vesselNodeExists = vesselNodes.some(v => v.id === `vessel-${n.data.vessel._id}`);

            return cargoNodeExists && vesselNodeExists;
        });

        setNodes([...cargoNodes, ...vesselNodes, ...connectionNodes]);

        setConnections(prevConnections =>
            prevConnections.filter(conn => {
                const cargoSelected = selectedCargo.includes(conn.cargo._id);
                const vesselSelected = selectedVessels.includes(conn.vessel._id);
                return cargoSelected && vesselSelected;
            })
        );

        setEdges(prevEdges =>
            prevEdges.filter(edge => {
                // Keep edge if both source and target are in the current node set
                const allNodeIds = new Set([
                    ...cargoNodes.map(n => n.id),
                    ...vesselNodes.map(n => n.id),
                    ...connectionNodes.map(n => n.id)
                ]);
                return allNodeIds.has(edge.source) && allNodeIds.has(edge.target);
            })
        );
    }, [selectedCargo, selectedVessels, cargoVesselList]);

    const deleteNode = useCallback((nodeId) => {
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));

        if (nodeId.startsWith("cargo-")) {
            const cargoId = nodeId.replace("cargo-", "");
            setSelectedCargo((prev) => prev.filter((id) => id !== cargoId));
        } else if (nodeId.startsWith("vessel-")) {
            const vesselId = nodeId.replace("vessel-", "");
            setSelectedVessels((prev) => prev.filter((id) => id !== vesselId));
        }

        setConnections((conns) =>
            conns.filter((conn) => {
                const cargoNodeId = `cargo-${conn.cargo._id}`;
                const vesselNodeId = `vessel-${conn.vessel._id}`;
                return nodeId !== cargoNodeId && nodeId !== vesselNodeId;
            })
        );
    }, [setNodes, setEdges, setSelectedCargo, setSelectedVessels, setConnections]);


    const toggleCargoSelection = (cargoId) => {
        setSelectedCargo(prev =>
            prev.includes(cargoId)
                ? prev.filter(id => id !== cargoId)
                : [...prev, cargoId]
        );
    };

    const toggleVesselSelection = (vesselId) => {
        setSelectedVessels(prev =>
            prev.includes(vesselId)
                ? prev.filter(id => id !== vesselId)
                : [...prev, vesselId]
        );
    };

    const onConnect = useCallback(
        async (params) => {
            const sourceNode = nodes.find((n) => n.id === params.source);
            const targetNode = nodes.find((n) => n.id === params.target);
            const uniqueId = uuidv4();

            if (!sourceNode || !targetNode) return;

            if (sourceNode.type === "connection" || targetNode.type === "connection") {
                return;
            }

            const connectionExists = edges.some(
                (edge) =>
                    (edge.source === params.source && edge.target === params.target) ||
                    (edge.source === params.target && edge.target === params.source)
            );

            if (connectionExists) {
                return;
            }

            if (
                (sourceNode.type === "cargo" && targetNode.type === "vessel") ||
                (sourceNode.type === "vessel" && targetNode.type === "cargo")
            ) {
                const connectionData = {
                    vesselId: uniqueId,
                    cargo: sourceNode.type === "cargo" ? sourceNode.data : targetNode.data,
                    vessel: sourceNode.type === "vessel" ? sourceNode.data : targetNode.data,
                    connectedAt: new Date().toISOString(),
                    loading: true,
                };

                const { userId, lastModifiedUser, createdUser, _id, __v, updatedAt, createdAt, ...cargoData } = connectionData.cargo;
                const {
                    _id: parseVesselId1,
                    userId: parseVesselUserId1,
                    createdUser: parseVesselCreatedUser1,
                    lastModifiedUser: parseVesselModifiedUser,
                    vesselExtraDetails,
                    __v: parseVessel__v1,
                    createdAt: parseVesselCreatedAt,
                    createdDate: parseVesselCreatedDate,
                    lastModifiedDate: parseVesselLastModifiedDate,
                    pattern: parseVesselPattern,
                    updatedAt: parseVesselUpdatedAt,
                    vesselLabel,
                    vesselAHL,
                    vesselCO2,
                    vesselCountry,
                    vesselGrabs,
                    vesselIceClass,
                    vesselIMO,
                    vesselLdnConsmMGO1,
                    vesselLdnConsmMGO2,
                    vesselLightShip,
                    vesselMMSI,
                    vesselType,
                    vesselSource,
                    vesselName,
                    ...parseVesselDetails
                } = connectionData.vessel.vessel && JSON.parse(connectionData.vessel.vessel);
                const {
                    _id: parseVesselIds,
                    vesselId: parseVesselId,
                    createdUser: parseVesselCreatedUser,
                    organizationId: parseVesselOrganizationId,
                    userId: parseVesselUserId,
                    lastModifiedUser: parseVesselLastModifiedUser,
                    vesselUniqueId: parseVesselVesselUniqueId,
                    __v: parseVessel__v,
                    ...rest
                } = connectionData.vessel;
                const payload = {
                    cargoExtraDetails: [],
                    cargoList: connectionData.cargo.cargoList,
                    clientQuery: cargoData,
                    fleetList: [{
                        ...rest,
                        vessel: JSON.stringify({
                            id: uniqueId,
                            ...parseVesselDetails,
                            system: true,
                        }),
                        vesselId: uniqueId,
                    }]
                }

                const midX = (sourceNode.position.x + targetNode.position.x) / 2;
                const midY = (sourceNode.position.y + targetNode.position.y) / 2;

                const connectionNodeId = `connection-${Date.now()}`;
                const connectionNode = {
                    id: connectionNodeId,
                    type: 'connection',
                    position: { x: midX, y: midY },
                    data: {
                        ...connectionData,
                        onDelete: deleteNode
                    },
                };
                setNodes((nds) => nds.concat(connectionNode));

                setEdges((eds) => [
                    ...eds,
                    {
                        id: `edge-${params.source}-${connectionNodeId}`,
                        source: params.source,
                        target: connectionNodeId,
                        animated: true,
                        type: "bezier",
                        style: { stroke: "#a855f7", strokeWidth: 2 },
                    },
                    {
                        id: `edge-${connectionNodeId}-${params.target}`,
                        source: connectionNodeId,
                        target: params.target,
                        animated: true,
                        type: "bezier",
                        style: { stroke: "#a855f7", strokeWidth: 2 },
                        markerEnd: { type: "arrowclosed", color: "#a855f7" },
                    },
                ]);

                try {
                    const apiResponse = await sendCalculatorData(payload);
                    connectionData.apiResponse = apiResponse;

                    setNodes((nds) =>
                        nds.map((n) =>
                            n.id === connectionNodeId
                                ? {
                                    ...n,
                                    data: {
                                        ...n.data,
                                        apiResponse,
                                        loading: false,
                                    },
                                }
                                : n
                        )
                    );

                    setConnections(prev => [...prev, { ...connectionData, apiResponse }]);
                } catch (error) {
                    console.error("API connection failed", error);

                    setNodes((nds) =>
                        nds.map((n) =>
                            n.id === connectionNodeId
                                ? {
                                    ...n,
                                    data: {
                                        ...n.data,
                                        loading: false,
                                        error: "Failed to load connection data",
                                    },
                                }
                                : n
                        )
                    );
                }
            }
        },
        [nodes, edges, setEdges, setNodes, deleteNode]
    );

    const clearCanvas = () => {
        setEdges([]);
        setConnections([]);
        setNodes((nds) => nds.filter((node) => node.type !== 'connection'));
    };



    const handleSubmit = (e) => {
        e.preventDefault();
        if (reason === "") {
            toastError("Please write a reason");
            return
        }
        const requestData = {
            reason: reason,
            createdAt: new Date().toISOString(),
        };

        updateUser({ request: requestData })
            .then((res) => {
                toastSuccess('Request submited successfully');
                setReason("");
            })
            .catch((err) => {
                console.error('Error updating user details:', err);
                toastError('Error updating user details');
            });
    };

    if (!user?.isPaid && isUser30DaysOld(user)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-slate-100 p-6">
                <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl text-center">
                    <div className="flex justify-center mb-4">
                        <LuLink2 className="text-sky-600" size={40} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">
                        Request Access
                    </h2>
                    <p className="text-sm text-slate-600 mb-6">
                        Get access to the Cargo-Vessel Matrix Diagram
                    </p>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter your reason..."
                        className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-sky-500 outline-none"
                        rows={4}
                    />
                    <button
                        onClick={handleSubmit}
                        className="mt-4 w-full bg-sky-600 hover:bg-sky-700 text-white py-2.5 rounded-lg font-semibold"
                    >
                        Submit Request
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="-mx-4 -my-2.5 h-[calc(100vh-47px)] md:mx-0 md:my-0 md:h-[calc(100vh-106px)] flex bg-gray-50 overflow-hidden relative">
            <div
                className={`bg-white border-r border-gray-200 transition-all duration-300 flex flex-col absolute md:static left-0 top-0 h-full z-[9] md:z-auto shadow-xl md:shadow-none ${leftSidebarOpen ? "w-56" : "w-0"
                    }`}
            >
                {leftSidebarOpen && (
                    <>
                        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 py-4">
                            <div className="flex items-center gap-2">
                                <LuPackage className="text-rose-600" size={18} />
                                <h3 className="font-semibold text-gray-800 text-sm">
                                    Cargo List
                                </h3>
                            </div>
                            <span className="text-[11px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                                {selectedCargo.length} selected
                            </span>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-2 scroll-thin">
                            {cargoVesselList.cargoList.map((cargo) => (
                                <div
                                    key={cargo._id}
                                    onClick={() => toggleCargoSelection(cargo._id)}
                                    className={`p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${selectedCargo.includes(cargo._id)
                                        ? "border-rose-400 bg-rose-50"
                                        : "border-gray-200 bg-white hover:border-rose-300"
                                        }`}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-900 mb-1 text-xs">
                                                {cargo.name}
                                            </h4>
                                            <div className="space-y-0.5 text-[11px] text-gray-600">
                                                <div className="flex items-center gap-1">
                                                    <LuTruck size={11} />
                                                    <span>{cargo.freightMode}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <LuPackage size={11} />
                                                    <span>{cargo.qtyMode}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {selectedCargo.includes(cargo._id) && (
                                            <div className="bg-rose-500 text-white rounded-full p-0.5">
                                                <LuCheck size={12} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* LEFT SIDEBAR TOGGLE BUTTON (INSIDE EDGE) */}
                <button
                    onClick={() => toggleLeftSidebar(!leftSidebarOpen)}
                    className="absolute top-1/2 left-[calc(14rem-12px)] -translate-y-1/2 bg-white border border-gray-200 rounded-full p-1.5 hover:bg-gray-100 transition-all z-10 shadow-sm"
                    style={{ display: leftSidebarOpen ? "flex" : "none" }}
                >
                    <LuChevronLeft size={18} />
                </button>
                {!leftSidebarOpen && (
                    <button
                        onClick={() => toggleLeftSidebar(true)}
                        className="absolute top-1/2 left-0 -translate-y-1/2 bg-white border border-gray-200 rounded-r-full p-1.5 hover:bg-gray-100 transition-all z-10 shadow-sm"
                    >
                        <LuChevronRight size={18} />
                    </button>
                )}
            </div>

            {/* CENTER REACT FLOW AREA */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-300">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-emerald-100 rounded-lg">
                                <LuLink2 className="text-emerald-600" size={18} />
                            </div>
                            <div>
                                <p className="text-[11px] text-gray-500">Active Connections</p>
                                <p className="text-sm font-semibold text-gray-800">
                                    {connections.length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={clearCanvas}
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-medium rounded-md transition-colors"
                    >
                        <LuTrash2 size={12} />
                        Reset
                    </button>
                </div>

                <div className="flex-1 bg-[#f3f3f3] overflow-hidden">
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        fitView
                        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                        minZoom={0.4}
                        maxZoom={1.5}
                        defaultEdgeOptions={{
                            type: "bezier",
                            animated: true,
                        }}
                    >
                        <Background gap={20} color="#000" />
                        <Controls className="bg-white border border-gray-300 rounded-lg shadow-sm" />
                        <MiniMap
                            nodeColor={(node) => {
                                if (node.type === "cargo") return "#fb7185";
                                if (node.type === "vessel") return "#a78bfa";
                                if (node.type === "connection") return "#34d399";
                                return "#9ca3af";
                            }}
                            maskColor="rgba(0,0,0,0.05)"
                            className="bg-white border border-gray-300 rounded-lg shadow-sm"
                        />
                    </ReactFlow>
                </div>

                <div className="bg-white border-t border-gray-200 px-3 py-1.5 text-center text-[11px] text-gray-600">
                    💡 <strong>Tip:</strong> Select cargo (left) and vessels (right), then
                    connect them in the diagram.
                </div>
            </div>

            {/* RIGHT SIDEBAR */}
            <div
                className={`bg-white border-l border-gray-200 transition-all duration-300 flex flex-col absolute md:static right-0 top-0 h-full z-[9] md:z-auto shadow-xl md:shadow-none ${rightSidebarOpen ? "w-56" : "w-0"
                    }`}
            >
                {rightSidebarOpen && (
                    <>
                        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 py-4">
                            <div className="flex items-center gap-2">
                                <LuShip className="text-sky-600" size={18} />
                                <h3 className="font-semibold text-gray-800 text-sm">
                                    Vessel List
                                </h3>
                            </div>
                            <span className="text-[11px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
                                {selectedVessels.length} selected
                            </span>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-2 scroll-thin">
                            {cargoVesselList.vesselList.map((vessel) => {
                                const vesselObj = JSON.parse(vessel.vessel);
                                return (
                                    <div
                                        key={vessel._id}
                                        onClick={() => toggleVesselSelection(vessel._id)}
                                        className={`p-2.5 rounded-lg border cursor-pointer transition-all text-sm ${selectedVessels.includes(vessel._id)
                                            ? "border-sky-400 bg-sky-50"
                                            : "border-gray-200 bg-white hover:border-sky-300"
                                            }`}
                                    >
                                        <div className="flex justify-between items-start gap-2">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900 mb-1 text-xs">
                                                    {vesselObj.vesselName}
                                                </h4>
                                                <div className="space-y-0.5 text-[11px] text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <LuAnchor size={11} />
                                                        <span>{vesselObj.vesselType}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <LuMapPin size={11} />
                                                        <span>{vessel.vesselOpeningPort}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {selectedVessels.includes(vessel._id) && (
                                                <div className="bg-sky-500 text-white rounded-full p-0.5">
                                                    <LuCheck size={12} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* RIGHT SIDEBAR TOGGLE BUTTON (INSIDE EDGE) */}
                <button
                    onClick={() => toggleRightSidebar(!rightSidebarOpen)}
                    className="absolute top-1/2 right-[calc(14rem-12px)] -translate-y-1/2 bg-white border border-gray-200 rounded-full p-1.5 hover:bg-gray-100 transition-all z-10 shadow-sm"
                    style={{ display: rightSidebarOpen ? "flex" : "none" }}
                >
                    <LuChevronRight size={18} />
                </button>
                {!rightSidebarOpen && (
                    <button
                        onClick={() => toggleRightSidebar(true)}
                        className="absolute top-1/2 right-0 -translate-y-1/2 bg-white border border-gray-200 rounded-l-full p-1.5 hover:bg-gray-100 transition-all z-10 shadow-sm"
                    >
                        <LuChevronLeft size={18} />
                    </button>
                )}
            </div>
        </div>
    );
}

export default memo(MatrixCurve);